/// <amd-module name="DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Components/PipelinesAuthorizationComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/SecurityComponent/Resources";
import * as Constants from "DistributedTaskUI/SecurityComponent/Constants";
import { PipelinesListComponent } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Components/PipelinesListComponent";
import { RestrictAllPipelinesConfirmationPanel } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Components/RestrictAllPipelinesConfirmationPanel";
import { AuthorizeAllPipelinesConfirmationDialog } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Components/AuthorizeAllPipelinesConfirmationDialog";
import { PipelinesAuthorizationStore, IPipelinesAuthorizationState } from "PipelinePermissionsAuthorization/Stores/PipelinesAuthorizationStore";
import { PipelinesAuthorizationActionsHub } from "PipelinePermissionsAuthorization/Actions/PipelinesAuthorizationActionsHub";
import { PipelinesAuthorizationActionsCreator } from "PipelinePermissionsAuthorization/Actions/PipelinesAuthorizationActionsCreator";
import { PipelinesAuthorizationSource } from "PipelinePermissionsAuthorization/Source/PipelinesAuthorizationSource";
import { PipelinePermission } from "PipelineChecks/Authorization/Client/Contracts/PipelinePermissions";
import { BuildDefinitionReference } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Source/Build/Contracts/Build";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { TitleSize, Header } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Table, ITableColumn, TableColumnStyle, SimpleTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { MenuButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";

export interface IPipelinesAuthorizationComponentProps {
    className?: string;
    resourceType: string;
    resourceId: string;
    noAuthorizedPipelinesViewLearnMoreLink?: string;
    noRestrictionsViewLearnMoreLink?: string;
    canOpenAccess: boolean;
    canRestrictAccess: boolean;
    canEditPipelinePermission?: boolean;
    hideRestrictPermissionButton?: boolean;
    customNoAuthorizedPipelinesCardContent?: string;
}

export class PipelinesAuthorizationComponent extends VssComponent<IPipelinesAuthorizationComponentProps, IPipelinesAuthorizationState>{
    private _performanceService: IVssPerformanceService;
    private _pipelinesAuthorizationComponentFeature: string = "PipelinesAuthorizationComponent";
    private _pipelinesAuthorizationComponentScenario: string = "PipelinesAuthorizationComponent.Loaded";
    private _loggedPerfEvent: boolean = false;
    
    constructor(props: IPipelinesAuthorizationComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._performanceService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._performanceService.startScenario(this._pipelinesAuthorizationComponentScenario, true);
        this._actionsHub = new PipelinesAuthorizationActionsHub();
        this._pipelinesAuthorizationSource = new PipelinesAuthorizationSource(this.context.pageContext);
        this._pipelinesAuthorizationActionsCreator = new PipelinesAuthorizationActionsCreator(this._actionsHub, this._pipelinesAuthorizationSource);
        this._pipelinesAuthorizationActionsCreator.initializeAuthorizedDefinitions(this.props.resourceType, this.props.resourceId);
        this._store = new PipelinesAuthorizationStore(this._actionsHub);
        this.state = this._store.getState();
        this._permissionsProvider = new ArrayItemProvider<BuildDefinitionReference>([]);
        this._expandToggleRestrictionPanel = new ObservableValue(false);
        this._showOpenAccessDialog = new ObservableValue(false);
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
        }
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
    }

    public componentDidUpdate(prevProps: any, prevState: any): void {
        if (this.state !== prevState && !this._loggedPerfEvent) {
            this._performanceService.endScenario(this._pipelinesAuthorizationComponentFeature, this._pipelinesAuthorizationComponentScenario, true);
            this._loggedPerfEvent = true;
        }
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
        this._store.dispose();
    }

    private _onStoreChanged = (): void => {
        let state = this._store.getState();
        if (state.authorizedPipelineDefinitions && state.authorizedPipelineDefinitions.length > 0) {
            this._permissionsProvider = new ArrayItemProvider<BuildDefinitionReference>(state.authorizedPipelineDefinitions);
        }
        
        this.setState(state);
    }

    public render(): React.ReactNode {
        if (this.state.authorizedDefinitions && !this.state.authorizedDefinitions.allPipelines) {
            return this._restrictionsView();
        } else {
            return this._noRestrictionsView();
        }
    }

    private _restrictionsView(): React.ReactNode {
        let items = this.state.allPipelineDefinitions.filter(bd => !this.state.authorizedPipelinesDictionary[bd.id]);
        let pipelinesDescription = (this.state.authorizedDefinitions.pipelines && this.state.authorizedDefinitions.pipelines.length > 0) ? Resources.PipelinePermissionsDescription : "" ;

        return (
            <div className={ this.props.className + " flex bolt-card flex-column"}>
                {   this.state.errorMessage &&
                    this._getErrorCardComponent()
                }
                <Card className="pipelines-permissions-card bolt-card-no-vertical-padding flex-column" 
                    contentProps={{ contentPadding: false, className: "pipelines-permissions-card-content" }}>
                    <h2 className="pipelines-permissions-card-header">
                        {Resources.PipelinePermissionsTitle} 
                    </h2>
                    <div className="pipelines-permissions-card-header-button-group">
                        <PipelinesListComponent 
                            items={items}
                            canEditPipelinePermission={this.props.canEditPipelinePermission}
                            onSelectionChanged={this._authorizePipeline}    
                        />
                        { this._getRestrictionsViewMenuButton() }
                    </div>
                    <div className="pipeline-permissions-card-description secondary-text">
                        {pipelinesDescription}
                    </div>
                    { (this.state.authorizedDefinitions.pipelines && this.state.authorizedDefinitions.pipelines.length > 0) && this._getPermissionsTableView() }
                    { (!this.state.authorizedDefinitions.pipelines || this.state.authorizedDefinitions.pipelines.length <= 0) && this._getNoAuthorizedPipelinesView() }
                </Card>
                { this._getToggleRestrictionDialog() }
            </div>
        );
    }

    private _noRestrictionsView(): React.ReactNode {
        return (
            <div className={ this.props.className + " flex bolt-card flex-column"}>
                {   this.state.errorMessage &&
                    this._getErrorCardComponent()
                }
                <Card className="pipelines-no-restrictions-permissions-card no-restrictions-card bolt-card-no-vertical-padding flex-column"
                    contentProps={{ contentPadding: false, className: "pipelines-no-restrictions-permissions-card-content" }}
                >
                    <h2 className="pipelines-no-restrictions-permissions-card-header">
                        {Resources.PipelinePermissionsTitle}
                    </h2>
                    {this.props.hideRestrictPermissionButton !== true && 
                        <Button
                            className="pipelines-no-restrictions-permissions-card-header-button"
                            id="restrict-pipeline-permission"
                            text={Resources.RestrictAccessButtonText}
                            disabled={!this.props.canRestrictAccess}
                            onClick={() => {
                                if (this.state.authorizedDefinitions.pipelines && this.state.authorizedDefinitions.pipelines.length > 0) {
                                    this._expandToggleRestrictionPanel.value = true;
                                } else {
                                    this._toggleRestrictAllPipelinesFlag(true);
                                }
                            }
                            }>
                        </Button>
                    }
                    <div className="no-restrictions-view justify-center">
                        <Icon iconName={"Unlock"} className={"no-restrictions-icon"} ariaLabel="" />
                        <div className="message-container">
                            <span className="message-header title-s">{ Resources.NoRestrictionsCardTitle }</span>
                            <span>{ Resources.NoRestrictionsCardContent }<br/> <Link ariaLabel={Resources.LearnMoreDescriptionForPipelinePermissions} href={this.props.noRestrictionsViewLearnMoreLink}>{ Resources.LearnMoreText }</Link></span>
                        </div>
                    </div>
                </Card>
                { this._getToggleRestrictionConfirmationPanel() }
            </div>
        );
    }

    private _getToggleRestrictionDialog(): React.ReactNode {
        return (
            <Observer isDialogOpen={this._showOpenAccessDialog}>
                {
                    (props: {isDialogOpen: boolean}) => {
                        return props.isDialogOpen ? (
                            <AuthorizeAllPipelinesConfirmationDialog 
                                onDismiss = {() => {
                                    this._showOpenAccessDialog.value = false;
                                    if (this._menuButtonRef.current) {
                                        this._menuButtonRef.current.focus();
                                    }
                                }}
                                primaryOnClick = {this._toggleRestrictAllPipelinesFlag}
                            />
                        ) : null;
                    }
                }
            </Observer>
        );
    }

    private _getToggleRestrictionConfirmationPanel(): React.ReactNode {
        return (
            <Observer isPanelOpen={this._expandToggleRestrictionPanel}>
                {
                    (props: {isPanelOpen: boolean}) => {
                        return props.isPanelOpen ? (
                            <RestrictAllPipelinesConfirmationPanel
                                pipelineDefinitions = {this.state.authorizedPipelineDefinitions}
                                primaryOnClick = { this._toggleRestrictAllPipelinesFlag } 
                                onDismiss = {() => {  this._expandToggleRestrictionPanel.value = false; }}
                            />
                        ) : null;
                    }
                }
            </Observer>
        );
    }

    private _getRestrictionsViewMenuButton(): React.ReactNode {
        return (
            <MenuButton
                className="bolt-header-command-item-button fontSizeML pipelines-permissions-card-header-menu-button"
                disabled={!this.props.canOpenAccess}
                contextualMenuProps={{
                    menuProps: {
                        id: "open-resource-menu",
                        items: [
                            {
                                id: "open-resource-menu-item",
                                iconProps: {
                                    iconName: "Unlock"
                                },
                                text:  Resources.OpenAccessMenuItem,
                                onActivate: () => {
                                    this._showOpenAccessDialog.value = true;
                                }
                            }
                        ]
                    }
                }}
                hideDropdownIcon={true}
                iconProps={{ iconName: "MoreVertical" }}
                subtle={true}
                ariaLabel={this.props.canOpenAccess ? Resources.ViewOptionsTooltip : Resources.NotAuthorizedOpenAccess}
                ref={this._menuButtonRef}
            />
        );
    }

    private _getNoAuthorizedPipelinesView(): React.ReactNode {
        let noAuthorizedPipelinesStrCustom = this.props.customNoAuthorizedPipelinesCardContent;
        let noAuthorizedPipelinesStrDefault = Resources.NoAuthorizedPipelinesCardContent;
        let noAuthorizedPipelinesStr = noAuthorizedPipelinesStrCustom ? noAuthorizedPipelinesStrCustom : noAuthorizedPipelinesStrDefault;
        Utils_Accessibility.announce(Resources.NoAuthorizedPipelinesCardTitle);
        return (
            <div className="no-restrictions-view justify-center">
                <Icon iconName={"Lock"} className={"no-restrictions-icon"} ariaLabel="" />
                <div className="message-container">
                    <span className="message-header title-s">{ Resources.NoAuthorizedPipelinesCardTitle }</span>
                    <span>{ noAuthorizedPipelinesStr } <br/> { Resources.NoAuthorizedPipelinesCardDesignerPipelinesContent } <br/> <Link ariaLabel={Resources.LearnMoreDescriptionForPipelinePermissions} href={this.props.noAuthorizedPipelinesViewLearnMoreLink}>{ Resources.LearnMoreText }</Link></span>
                </div>
            </div>
        );
    }

    private _getPermissionsTableView(): React.ReactNode {
        return (
            <Table
                ariaLabel={Resources.PipelinePermissionsTableName}
                columns={this._getTableColumns()}
                itemProvider={this._permissionsProvider}
                scrollable={true}
                role="presentation"
            />
        );
    }

    private _getTableColumns = (): ITableColumn<any>[] => {
        return [
            {
                id: "name",
                width: -80,
                name: Resources.PipelineColumnHeading,
                className: "pipeline-name-cell",
                renderCell: this._renderNameCell,
                columnStyle: TableColumnStyle.Primary
            },
            {
                id: "menu",
                width: -20,
                className: "menu-item-cell",
                renderCell: this._renderMenuItemCell
            }
        ];
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<any>, tableItem: BuildDefinitionReference): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Icon className="pipeline-image" iconName="Rocket" role="presentation" />
                <span>
                    <Tooltip overflowOnly={true} text={tableItem.name}>
                        <span className="pipeline-name text-ellipsis">{tableItem.name}</span>
                    </Tooltip>
                    <span className="pipeline-type secondary-text body-s">
                        {Resources.PipelineString}
                    </span>
                </span>
            </SimpleTableCell>
        );
    }

    private _renderMenuItemCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<any>, tableItem: BuildDefinitionReference): JSX.Element => {
        let removePermission = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            this._unAuthorizePipeline(tableItem);
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Button tooltipProps={{text: Resources.RevokeAccessTooltip}} primary={false} className="bolt-table-cell-content-reveal pipeline-removal-button" subtle={true} iconProps={{iconName: "Cancel"}} ariaLabel={Resources.Removepermission} onClick={removePermission} />
            </SimpleTableCell>
        );
    }

    private _unAuthorizePipeline(definitionReference: BuildDefinitionReference): void {
        let modifiedAuthorizedDefinitions = this.state.authorizedDefinitions;
        modifiedAuthorizedDefinitions.pipelines.forEach((pipeline) => {
            if (definitionReference.id === pipeline.id) {
                pipeline.authorized = false;
            }
        });
        
        this._pipelinesAuthorizationActionsCreator.updatePipelinePermisionsForResource(this.props.resourceType, this.props.resourceId, modifiedAuthorizedDefinitions);
        this._publishTelemetry(Constants.FeatureNames.RemovePipeline);
    }

    private _authorizePipeline = (pipelineReference: any): void => {
        let modifiedAuthorizedDefinitions = this.state.authorizedDefinitions;
        let newPipelineReference: PipelinePermission = {
            authorized: true,
            authorizedBy: null,
            authorizedOn: null,
            id: pipelineReference.selectedItems[0].id
        };

        if (modifiedAuthorizedDefinitions.pipelines) {
            modifiedAuthorizedDefinitions.pipelines.push(newPipelineReference);
        } else {
            modifiedAuthorizedDefinitions.pipelines = [newPipelineReference];
        }
        
        this._pipelinesAuthorizationActionsCreator.updatePipelinePermisionsForResource(this.props.resourceType, this.props.resourceId, modifiedAuthorizedDefinitions);
        this._publishTelemetry(Constants.FeatureNames.AddPipeline);
    }

    private _toggleRestrictAllPipelinesFlag = (restrict: boolean): void => {
        let modifiedAuthorizedDefinitions = Object.assign({}, this.state.authorizedDefinitions);
        modifiedAuthorizedDefinitions.allPipelines = {authorized: !restrict, authorizedBy: null, authorizedOn: null};
        this._pipelinesAuthorizationActionsCreator.toggleAllPipelinesRestriction(this.props.resourceType, this.props.resourceId, modifiedAuthorizedDefinitions);
        this._showOpenAccessDialog.value = false;
        this._expandToggleRestrictionPanel.value = false;
        if (restrict) {
            this._publishTelemetry(Constants.FeatureNames.RestrictAccess);
        } else {
            this._publishTelemetry(Constants.FeatureNames.OpenAccess);
        }
    }

    private _getErrorCardComponent(): JSX.Element {
        return (
            <MessageCard className="error-message-card" onDismiss={() => { this._pipelinesAuthorizationActionsCreator.setErrorMessage(""); }} severity={MessageCardSeverity.Error}>
                {this.state.errorMessage}
            </MessageCard>
        );
    }

    private _publishTelemetry(featureName: string): void {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        const projectAndResourceIdentifier = format("{0}:{1}:{2}", this._projectId, this.props.resourceType, this.props.resourceId);
        const properties = {
            ProjectAndResourceIdentifier: projectAndResourceIdentifier
        };

        telemetryService.publishEvent(Constants.Area, featureName, properties);
    }

    private _store: PipelinesAuthorizationStore;
    private _actionsHub: PipelinesAuthorizationActionsHub;
    private _permissionsProvider: ArrayItemProvider<any>;
    private _pipelinesAuthorizationActionsCreator: PipelinesAuthorizationActionsCreator;
    private _pipelinesAuthorizationSource: PipelinesAuthorizationSource;
    private _expandToggleRestrictionPanel: ObservableValue<boolean>;
    private _showOpenAccessDialog: ObservableValue<boolean>;
    private _projectId: string;
    private _menuButtonRef = React.createRef<MenuButton>();
}

VssComponent.register("pipelines-authorization-component", PipelinesAuthorizationComponent);